import { Box, Text } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { TaskFile } from 'modules/tasks/hooks/useGetTaskFiles'

import { TaskDetail } from '../../TaskElement/useTaskDetail'
import Attachments from '../Attachments'
import { Comments } from '../Comments'
import DataCollection from '../DataCollection'
import DocumentSignatures from '../DocumentSignatures'
import UploadDocumentsTypeAttachments from '../UploadDocumentsTypeAttachments'
import * as styles from './index.vanilla.css'

interface Props {
  loadingFiles: boolean
  task: TaskDetail
  files: TaskFile[]
  owner: User | undefined
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
  contractVersions: ContractVersions
  canEmployeeManageAnyEmployeeFolder: boolean
}

const Content = ({
  task,
  files,
  loadingFiles,
  owner,
  canSeePersonalInfo,
  canEditPersonalInfo,
  contractVersions,
  canEmployeeManageAnyEmployeeFolder,
}: Props) => {
  const content = task.content

  const superTask = () => {
    const type = 'type' in task ? task.type : null
    switch (type) {
      case 'sign_documents':
        return <DocumentSignatures task={task} owner={owner} contractVersions={contractVersions} />
      case 'send_document_template':
        return <DocumentSignatures task={task} owner={owner} contractVersions={contractVersions} />
      case 'upload_document':
        return (
          <UploadDocumentsTypeAttachments
            task={task}
            canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          />
        )
      case 'data_collection':
        return (
          <DataCollection
            task={task}
            canSeePersonalInfo={canSeePersonalInfo}
            canEditPersonalInfo={canEditPersonalInfo}
            canEmployeeManageAnyEmployeeFolder={canEmployeeManageAnyEmployeeFolder}
          />
        )
      default:
        return null
    }
  }

  return (
    <Box gap="s40">
      <Box gap="s16" width="full" className={styles.textWidth}>
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t('tasks.labels.content')}
        </Text.Normal>
        {content ? ( // Ignoring the semgrep comment, since we are sanitizing this both in frontend and in backend.
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(content),
            }}
          />
        ) : (
          <span>-</span>
        )}
      </Box>
      <Attachments files={files} loadingFiles={loadingFiles} />
      {superTask()}
      <Comments task={task} />
    </Box>
  )
}

export default observer(Content)
